/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { Seconds, TimeUtils } from '@wit/vtv-x-foundation-lib';
import React from 'react';
import { useSelector } from 'react-redux';
import translate from 'services/i18n/translate';
import { convertHMS } from 'services/utils/DateTimeUtils';
import { Selectors as PlayerSelectors } from 'store/ducks/player';
import { EDataType, formatBytes } from '../../../../services/utils/formatBytes';

export interface IPlayerStats {
  currentTime?: number;
  bufferedTotalEnd?: number;
  bufferedAudioEnd?: number;
  bufferedTextEnd?: number;
  bufferedVideoEnd?: number;
  bufferingTime?: string;
  droppedFrames?: string;
  licenseTime?: string;
  estimatedBandwidth?: number;
  streamBandwidth?: number;
  resolution?: string;
  jsHeapSizeLimit?: number;
  totalJSHeapSize?: number;
  usedJSHeapSize?: number;
}

interface IStatsForNerdsProps {
  stats?: IPlayerStats;
}

const StatsForNerds = (props: IStatsForNerdsProps): JSX.Element => {
  const { stats } = props;
  const isLivePosition = useSelector(PlayerSelectors.isLivePosition);

  const getFormatedDate = (seconds: Seconds, locale?: string): string => {
    const milliseconds = TimeUtils.secondsToMilliseconds(seconds);
    if (isLivePosition) {
      return new Date(milliseconds).toLocaleTimeString(locale || translate.getLanguage(), {
        hour12: false,
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        fractionalSecondDigits: 3,
      });
    }
    return convertHMS(seconds);
  };

  const currentTime = stats?.currentTime
    ? `${getFormatedDate(stats.currentTime)} | ${stats?.currentTime.toFixed(3)}`
    : undefined;

  const bufferedTotalEnd = stats?.bufferedTotalEnd
    ? `${getFormatedDate(stats.bufferedTotalEnd)} | ${stats?.bufferedTotalEnd.toFixed(3)}`
    : undefined;

  const bufferedAudioEnd = stats?.bufferedAudioEnd
    ? `${getFormatedDate(stats.bufferedAudioEnd)} | ${stats?.bufferedAudioEnd.toFixed(3)}`
    : undefined;

  const bufferedTextEnd = stats?.bufferedTextEnd
    ? `${getFormatedDate(stats.bufferedTextEnd)} | ${stats.bufferedTextEnd.toFixed(3)}`
    : undefined;

  const bufferedVideoEnd = stats?.bufferedVideoEnd
    ? `${getFormatedDate(stats.bufferedVideoEnd)} | ${stats.bufferedVideoEnd.toFixed(3)}`
    : undefined;

  const estimatedBandwidth = stats?.estimatedBandwidth
    ? `${formatBytes(stats?.estimatedBandwidth, EDataType.BITS)}/s`
    : undefined;

  const bufferingTime = stats?.bufferingTime ? `${Number(stats?.bufferingTime).toFixed(2)} s` : undefined;
  const droppedFrames = stats?.droppedFrames || undefined;
  const licenseTime = stats?.licenseTime ? `${Number(stats?.licenseTime).toFixed(2)} s` : undefined;
  const streamBandwidth = stats?.streamBandwidth
    ? `${formatBytes(stats?.streamBandwidth, EDataType.BITS)}/s`
    : undefined;
  const jsHeapSizeLimit = stats?.jsHeapSizeLimit ? formatBytes(stats?.jsHeapSizeLimit) : undefined;
  const totalJSHeapSize = stats?.totalJSHeapSize ? formatBytes(stats?.totalJSHeapSize) : undefined;
  const usedJSHeapSize = stats?.usedJSHeapSize ? formatBytes(stats?.usedJSHeapSize) : undefined;

  return stats ? (
    <div className="stats-overlay">
      <ul className="stats-list">
        {currentTime ? (
          <li>
            <span className="label">Current Time: </span>
            <span className="value">{currentTime}</span>
          </li>
        ) : null}

        {bufferedTotalEnd ? (
          <li>
            <span className="label">Buffered Total End: </span>
            <span className="value">{bufferedTotalEnd}</span>
          </li>
        ) : null}

        {bufferedAudioEnd ? (
          <li>
            <span className="label">Buffered Audio End: </span>
            <span className="value">{bufferedAudioEnd}</span>
          </li>
        ) : null}

        {bufferedTextEnd ? (
          <li>
            <span className="label">Buffered Text End: </span>
            <span className="value">{bufferedTextEnd}</span>
          </li>
        ) : null}

        {bufferedVideoEnd ? (
          <li>
            <span className="label">Buffered Video End: </span>
            <span className="value">{bufferedVideoEnd}</span>
          </li>
        ) : null}

        {bufferingTime ? (
          <li>
            <span className="label">Buffering Time: </span>
            <span className="value">{bufferingTime}</span>
          </li>
        ) : null}

        {droppedFrames ? (
          <li>
            <span className="label">Dropped Frames: </span>
            <span className="value">{droppedFrames}</span>
          </li>
        ) : null}

        {licenseTime ? (
          <li>
            <span className="label">License Time: </span>
            <span className="value">{licenseTime}</span>
          </li>
        ) : null}

        {estimatedBandwidth ? (
          <li>
            <span className="label">Estimated Bandwidth: </span>
            <span className="value">{estimatedBandwidth}</span>
          </li>
        ) : null}

        {streamBandwidth ? (
          <li>
            <span className="label">Stream Bandwidth: </span>
            <span className="value">{streamBandwidth}</span>
          </li>
        ) : null}
        {stats?.resolution ? (
          <li>
            <span className="label">Resolution: </span>
            <span className="value">{stats?.resolution}</span>
          </li>
        ) : null}
        {jsHeapSizeLimit ? (
          <li>
            <span className="label">JS HeapSize Limit: </span>
            <span className="value">{jsHeapSizeLimit}</span>
          </li>
        ) : null}

        {totalJSHeapSize ? (
          <li>
            <span className="label">Total JS HeapSize: </span>
            <span className="value">{totalJSHeapSize}</span>
          </li>
        ) : null}

        {usedJSHeapSize ? (
          <li>
            <span className="label">Used JS HeapSize: </span>
            <span className="value">{usedJSHeapSize}</span>
          </li>
        ) : null}
      </ul>
    </div>
  ) : (
    <></>
  );
};

export default StatsForNerds;
